// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Vex/rhythmCheck.js
//
// An extension of VexFlow for uTheory.com to mark early/late/right/wrong
// for rhythm performances
// eslint-disable-next-line import/no-cycle
import type { StaveNote, SVGContext } from 'vexflow';
import { Glyph } from 'vexflow';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import initGlyphs from './customGlyphs'; // the cycle is purely a type cycle

export interface RhythmCheckOptions {
  color?: string;
  /** x pixels to offset */
  offset?: number;
  /** Vex counting -- defaults to 5, 1st ledger below clef */
  staffLine?: number;
}

export type RhythmCheckStatusString =
  | 'missed'
  | 'veryEarly'
  | 'early'
  | 'green'
  | 'late'
  | 'veryLate';

type RCGlyphInfo = [number, string, string, number, boolean];

class RhythmCheck {
  // Public constructor
  constructor(
    noteHolder: NoteHolder,
    label: RhythmCheckStatusString,
    options: RhythmCheckOptions = {}
  ) {
    this.noteHolder = noteHolder;
    this.options = options;
    // Adjust for the notehead positioning if needed:
    /*
    if(typeof this.options.offset !== "undefined" &&
      this.options.offset < 0) {
      console.log("spacing left");
      this.options.offset -= (noteHolder.staveNote.getNoteHeadBeginX() +
            noteHolder.staveNote.getNoteHeadEndX());
    }
*/

    const green = 'green';
    const yellow = '#FF6E00';
    const red = 'red';

    const codeHead = noteHolder.staveNote!.glyph.code_head;
    // console.log({ glyph: noteHolder.staveNote!.glyph });
    this.glyphData = {
      // "label" : [offset, glyphName, color, fontSize, recenter]
      check: [0, 'checkMark', green, 28, true],
      green: [0, 'checkMark', green, 28, true],
      early: [-7, codeHead, yellow, 35, false],
      late: [7, codeHead, yellow, 35, false],
      veryEarly: [-14.5, codeHead, red, 35, false],
      veryLate: [14.5, codeHead, red, 35, false],
      missed: [0, 'xMark', red, 25, true],
      x: [0, 'xMark', red, 25, true],
    };

    this.label = label;

    initGlyphs();
  }

  public noteHolder: NoteHolder;

  private options: {
    color?: string;
    offset?: number;
    staffLine?: number;
    // Starting x position
    x?: number;
  };

  private glyphData: {
    check: RCGlyphInfo;
    green: RCGlyphInfo;
    early: RCGlyphInfo;
    late: RCGlyphInfo;
    veryEarly: RCGlyphInfo;
    veryLate: RCGlyphInfo;
    missed: RCGlyphInfo;
    x: RCGlyphInfo;
  };

  private label: RhythmCheckStatusString;

  private linePath: SVGGElement | false = false;

  private glyphPath: SVGGElement | false = false;

  // private group?: SVGGElement;

  disconnectLine() {
    if (this.label === 'early') {
      this.label = 'veryEarly';
      this.options.color = 'red';
      this.delete();
      return true;
    }
    if (this.label === 'late') {
      this.label = 'veryLate';
      this.options.color = 'red';
      this.delete();
      return true;
    }
    return false;
  }

  delete() {
    if (this.linePath) {
      this.linePath.parentElement?.removeChild(this.linePath);
      this.linePath = false;
    }
    if (this.glyphPath) {
      this.glyphPath.parentElement?.removeChild(this.glyphPath);
      this.glyphPath = false;
    }

    const el = this.noteHolder.staveNote!.getAttribute('el');
    // console.log({ el });
    if (!el) return;
    const path = el.querySelector('.vf-notehead')?.firstChild;
    if (!path) return;
    path.style.stroke = 'black';
    path.style.fill = 'black';
  }

  changeNoteHeadColor(color: string) {
    (this.noteHolder.staveNote as StaveNote).setKeyStyle(0, {
      fillStyle: color,
      strokeStyle: color,
    });
  }

  draw() {
    this.delete();
    const noteHolder = this.noteHolder;
    const staveNote = noteHolder.staveNote as StaveNote;
    if (!staveNote) return;
    const ctx = staveNote.getContext() as SVGContext;

    const info = this.glyphData[this.label];
    const code = info[1];
    const color = this.options.color || info[2];
    const offset = typeof this.options.offset === 'undefined' ? info[0] : this.options.offset;
    const recenter = info[4];
    const point = info[3]; // font size.
    let staffLine = staveNote.getKeyLine(0);
    // Given as music theory lines -- convert to Vex Line here:
    staffLine = -staffLine + 5;
    const originalStaffLine = staffLine;
    staffLine = staveNote.getStemDirection() > 0 ? staffLine + 1 : staffLine - 1;
    staffLine = this.options.staffLine || staffLine;

    // // If the label's green, change the notehead color:
    if (this.label === 'green') {
      const path = staveNote.getAttribute('el').querySelector('.vf-notehead').firstChild;
      path.style.stroke = 'green';
      path.style.fill = 'green';
      return;
    }

    // Start is middle of new notehead:
    const halfWidth = (staveNote.getNoteHeadEndX() - staveNote.getNoteHeadBeginX()) / 2;

    let x = staveNote.getNoteHeadBeginX() + offset;
    x = recenter ? x + halfWidth : x;
    x = typeof this.options.x === 'undefined' ? x : this.options.x;

    const stave = staveNote.getStave();
    if (!stave) return; // This should never happen.

    const y = stave.getYForLine(staffLine);
    // console.log({ Glyph });
    const glyph = new Glyph(code, point);
    glyph.setStyle({
      strokeStyle: color,
      fillStyle: color,
    });
    this.glyphPath = ctx.openGroup('rc-glyph');
    glyph.render(ctx, x, y);
    ctx.closeGroup();

    if (this.label === 'veryEarly' || this.label === 'veryLate' || this.label === 'missed') return;

    ctx.openGroup();
    // Draw a line between them:
    ctx.save();
    ctx.setLineDash([2, 2]);
    ctx.setStrokeStyle(color);
    ctx.setLineWidth(1);

    const x1 = x + halfWidth;
    const x2 = staveNote.getNoteHeadBeginX() + halfWidth;

    const y2 = stave.getYForLine(originalStaffLine);
    const y1 = stave.getYForLine(staffLine);

    this.linePath = ctx.openGroup('rc-line');
    ctx.beginPath().moveTo(x1, y1).lineTo(x2, y2).stroke();
    ctx.closeGroup();

    ctx.restore();
    ctx.closeGroup();
    // Cover up the line:
    this.changeNoteHeadColor('black');
  }
}

export default RhythmCheck;
